import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import type { SxProps, Theme } from '@mui/material/styles';

export interface BaseModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly ariaLabelledBy?: string;
  readonly maxWidth?: number | string;
  readonly sx?: SxProps<Theme>;
}

const defaultStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100% - 32px)', sm: 520 },
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 3,
};

/**
 * Reusable modal container component.
 * Accepts children for flexible content (forms, confirmations, etc.).
 */
export function BaseModal({ open, onClose, children, ariaLabelledBy, maxWidth, sx }: BaseModalProps) {
  const mergedStyle = {
    ...defaultStyle,
    ...(maxWidth ? { width: { xs: 'calc(100% - 32px)', sm: maxWidth } } : {}),
  };

  const finalSx: SxProps<Theme> = sx ? [mergedStyle, ...(Array.isArray(sx) ? sx : [sx])] : mergedStyle;

  return (
    <Modal open={open} onClose={onClose} aria-labelledby={ariaLabelledBy}>
      <Box sx={finalSx}>{children}</Box>
    </Modal>
  );
}

export default BaseModal;
export { ConfirmationDialog } from './ConfirmationDialog';
export type { ConfirmationDialogProps } from './ConfirmationDialog';
